import type { OPCS4Code } from "@/clinical-codes/types/ClinicalCodeTypes";
import type { Icd10Code } from "@/clinical-codes/useSearchIcd10Codes";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { useQuery } from "@tanstack/react-query";
import { omit } from "lodash";
import { useCallback, useEffect, useState } from "react";

export type ExpectedIcd10Code = Tables<"expected_diagnosis"> & {
  icd_10_code: Icd10Code;
};

export const useMedicalTextCodes = (medicalTextId?: string) => {
  const [selectedProcedures, setSelectedProcedures] = useState<OPCS4Code[]>([]);
  const [selectedDiagnoses, setSelectedDiagnoses] = useState<ExpectedIcd10Code[]>([]);
  const [selectedCoMorbidities, setSelectedCoMorbidities] = useState<ExpectedIcd10Code[]>([]);

  const { data, isFetching } = useQuery({
    queryKey: ["medical-text-codes", medicalTextId],
    queryFn: async () => {
      if (!medicalTextId) return null;

      // Fetch procedures
      const { data: proceduresData, error: proceduresError } = await supabase
        .from("expected_procedures")
        .select(
          `
          is_optional,
          order_index,
          opcs4_codes!inner(id, code, description)
        `,
        )
        .eq("medical_text_example_id", medicalTextId)
        .order("order_index");

      if (proceduresError) throw proceduresError;

      // Fetch diagnoses
      const { data: diagnosesData, error: diagnosesError } = await supabase
        .from("expected_diagnosis")
        .select(
          `
          *,
          icd10_codes:icd10_codes!inner
            (
              *
            )
        `,
        )
        .eq("medical_text_example_id", medicalTextId)
        .order("order_index");

      if (diagnosesError) throw diagnosesError;

      // Fetch co-morbidities
      const { data: coMorbiditiesData, error: coMorbiditiesError } = await supabase
        .from("expected_co_morbidities")
        .select(
          `
            *,
            icd10_codes:icd10_codes!inner(
              *
            )
        `,
        )
        .eq("medical_text_example_id", medicalTextId);

      if (coMorbiditiesError) throw coMorbiditiesError;

      // Transform procedures
      const procedures: OPCS4Code[] =
        proceduresData?.map((item) => ({
          id: item.opcs4_codes.id,
          code: item.opcs4_codes.code,
          description: item.opcs4_codes.description,
        })) || [];

      // Transform diagnoses
      const diagnoses: ExpectedIcd10Code[] =
        diagnosesData?.map((item) => ({
          ...omit(item, "icd10_codes"),
          icd_10_code: {
            ...item.icd10_codes,
            description: item.icd10_codes.description,
          },
        })) || [];

      // Transform co-morbidities
      const coMorbidities: ExpectedIcd10Code[] =
        coMorbiditiesData?.map((item, index) => ({
          ...omit(item, "icd10_codes"),
          order_index: index,
          icd_10_code: {
            ...item.icd10_codes,
            description: item.icd10_codes.description,
          },
        })) || [];

      return { procedures, diagnoses, coMorbidities };
    },
    enabled: !!medicalTextId,
  });

  // Set the fetched data when available
  useEffect(() => {
    if (data) {
      setSelectedProcedures(data.procedures);
      setSelectedDiagnoses(data.diagnoses);
      setSelectedCoMorbidities(data.coMorbidities);
    }
  }, [data]);

  const handleProceduresChange = useCallback((procedures: OPCS4Code[]) => {
    setSelectedProcedures(procedures);
  }, []);

  const handleDiagnosisCodeSelectionChange = useCallback(
    (codes: ExpectedIcd10Code[], type: "diagnoses" | "co-morbidities") => {
      switch (type) {
        case "diagnoses":
          setSelectedDiagnoses(codes);
          break;
        case "co-morbidities":
          setSelectedCoMorbidities(codes);
          break;
      }
    },
    [],
  );

  const toggleOptionalStatus = useCallback((codeId: string, type: "diagnoses" | "co-morbidities") => {
    const updateCodes = (codes: ExpectedIcd10Code[]) => {
      return codes.map((code) => (code.id === codeId ? { ...code, is_optional: !code.is_optional } : code));
    };

    switch (type) {
      case "diagnoses":
        setSelectedDiagnoses(updateCodes);
        break;
      case "co-morbidities":
        setSelectedCoMorbidities(updateCodes);
        break;
    }
  }, []);

  return {
    selectedProcedures,
    selectedDiagnoses,
    selectedCoMorbidities,
    handleDiagnosisCodeSelectionChange,
    handleProceduresChange,
    toggleOptionalStatus,
    isFetching,
  };
};
